import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, useMemo } from 'react';
import { useVariants } from '../../../hooks/tailwindThemeProvider';

import { cn } from '../utils';

const sidebarVariants = cva(
  'relative flex flex-col gap-2 h-screen p-4 bg-background border-r transition-all duration-300 ease-in-out',
  {
    variants: {
      variant: {
        default: 'border-r',
        floating: 'rounded-lg border border-border shadow-lg m-2 h-[calc(100vh-1rem)]',
      },
      size: {
        default: 'w-64',
        sm: 'w-48',
        lg: 'w-72',
      },
      collapsed: {
        true: 'w-16',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      collapsed: false,
    },
  }
);

interface SidebarProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof sidebarVariants> {
  fixed?: boolean;
}

const Sidebar = forwardRef<HTMLDivElement, SidebarProps>(
  ({ className, variant, size, collapsed, fixed, ...props }, ref) => {
    const { variants } = useVariants();

    const SidebarVariants: typeof sidebarVariants = useMemo(() => {
      return cva(variants.sidebar.defaultStyles, {
        variants: variants.sidebar.variantStyles,
        defaultVariants: variants.sidebar.defaultVariants as any,
      });
    }, [variants]);

    return (
      <div
        ref={ref}
        className={cn(
          SidebarVariants({ variant, size, collapsed }),
          fixed && 'fixed inset-y-0 left-0',
          className
        )}
        {...props}
      />
    );
  }
);
Sidebar.displayName = 'Sidebar';

const SidebarHeader = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center justify-center py-4', className)} {...props} />
  )
);
SidebarHeader.displayName = 'SidebarHeader';

const SidebarNav = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <nav ref={ref} className={cn('flex-1 space-y-1', className)} {...props} />
  )
);
SidebarNav.displayName = 'SidebarNav';

const sidebarItemVariants = cva(
  'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-transparent hover:text-foreground/80',
        active: 'bg-accent text-accent-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface SidebarItemProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof sidebarItemVariants> {
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  active?: boolean;
}

const SidebarItem = forwardRef<HTMLDivElement, SidebarItemProps>(
  ({ className, children, icon, badge, variant, active, ...props }, ref) => {
    const { variants } = useVariants();

    const SidebarItemVariants: typeof sidebarItemVariants = useMemo(() => {
      return cva(variants.sidebarItem.defaultStyles, {
        variants: variants.sidebarItem.variantStyles,
        defaultVariants: variants.sidebarItem.defaultVariants as any,
      });
    }, [variants]);

    return (
      <div
        ref={ref}
        className={cn(
          SidebarItemVariants({
            variant: active ? 'active' : variant,
          }),
          className
        )}
        {...props}
      >
        {icon && <span className="shrink-0">{icon}</span>}
        <span className="flex-1 truncate">{children}</span>
        {badge && <span>{badge}</span>}
      </div>
    );
  }
);
SidebarItem.displayName = 'SidebarItem';

const SidebarGroup = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('space-y-3 py-2', className)} role="group" {...props} />
  )
);
SidebarGroup.displayName = 'SidebarGroup';

const SidebarFooter = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('mt-auto', className)} {...props} />
  )
);
SidebarFooter.displayName = 'SidebarFooter';

export { Sidebar, SidebarFooter, SidebarGroup, SidebarHeader, SidebarItem, SidebarNav };
